/* globals SALESFORCE_SCRIPT_URL */
/* eslint-disable jsx-a11y/anchor-is-valid, no-script-url, class-methods-use-this */
import React from 'react';
import PropTypes from 'prop-types';
import {action, observable, reaction} from 'mobx';
import {observer, inject} from 'mobx-react';
import {withRouter, locationShape} from 'react-router';
import autobind from 'autobind-decorator';

import {browserOnly} from 'spa/Components/EnvSplit';
import {withConfig, withI18n} from './Context';

import {cn} from 'spa/css';

import Popup from 'spa/Components/Popup';

import closeIcon from 'spa/images/icons/cancel-medium-light.svg';
import alertIcon from 'spa/images/icons/alert-big.svg';

import './salesforce.less';

const LIVEAGENT_INIT_ID_1 = '5720N0000008aBN';
const LIVEAGENT_INIT_ID_2 = '00D0N000000gJ18';

@browserOnly
@withRouter
@inject(({
  store: {
    initialLoadComplete,
    screen,
    session,
    tracker,
  },
}) => (
  {
    initialLoadComplete,
    screen,
    session,
    tracker,
  }
))
@observer
@withI18n
@withConfig
class ChatButton extends React.Component {
  static propTypes = {
    initialLoadComplete: PropTypes.bool,
    location: locationShape.isRequired,
    screen: PropTypes.shape({
      isDesktop: PropTypes.bool,
    }).isRequired,
    session: PropTypes.object,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
  };

  UNSAFE_componentWillMount() {
    const {config, i18n} = this.context;
    const {location} = this.props;

    if (location && location.pathname.indexOf('/lp/') >= 0) {
      this.isLandingPage = true;
    }

    this.salesforceButtonId = i18n._x('5730N0000008aRn', 'Chat button - Salesforce ID');

    this.faqUrl = config.getValue('b2c.faq-url', i18n.umRegion);
    this.zendeskLocale = config.getValue('locale.zendesk-locale-id', i18n.umRegion);

    this.csWorkingHours = config.getValue('locale.cs-online-working-hours', i18n.umRegion);
  }

  componentDidMount() {
    if (!window._laq) { window._laq = []; }

    const {session} = this.props;
    const {user} = session;

    this.listeners = [
      reaction(
        () => this.props.initialLoadComplete,
        (initialLoadComplete) => {
          if (initialLoadComplete) {
            const userId = (user && user.id) || undefined;

            this.timeout = setTimeout(() => {
              this.loadChat(userId);
            }, 2000);
          }
        },
        {
          fireImmediately: true,
          name: 'Check if content has finished loading',
        },
      ),
      reaction(
        () => session && session.user && session.user.id,
        (id) => {
          this.loadChat(id);
        },
        {
          name: 'Create chat session when user signs in',
        },
      ),
    ];
  }

  componentWillUnmount() {
    delete window.liveagent;
    delete window.liveAgentDeployment;

    // This mock-agent suppresses errors caused by pending avail-checks
    window.liveagent = {_: {handlePing: () => { }}};

    while (this.listeners.length) {
      this.listeners.pop()();
    }

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
  }

  @observable isUserChat = false;
  @observable showChatOfflinePopup = false;
  @observable isChatOpen = false;
  @observable hasStartedChat = false;

  /*
   *  This function adds a new script from url parameter to the document head and allows a callback
   *  to be called once the script loads
   */
  loadScript(url, callback) {
    const script = document.createElement('script');
    script.type = 'text/javascript';

    if (script.readyState) { //IE
      script.onreadystatechange = () => {
        if (
          script.readyState === 'loaded' ||
          script.readyState === 'complete'
        ) {
          script.onreadystatechange = null;
          callback();
        }
      };
    } else { //Others
      script.onload = () => {
        callback();
      };
    }

    script.src = url;
    document.getElementsByTagName('head')[0].appendChild(script);
  }

  /*
   *  Reload the Salesforce Live Agent
   */
  @autobind
  loadChat(userId) {
    // We need to delete all the liveagent objects
    delete window.liveagent;
    delete window.liveAgentDeployment;

    // This mock-agent suppresses errors caused by pending avail-checks
    window.liveagent = {_: {handlePing: () => { }}};

    this.loadScript(`https://c.${SALESFORCE_SCRIPT_URL}.salesforceliveagent.com/content/g/js/42.0/deployment.js`, () => {
      if (window.liveagent && typeof window.liveagent.showWhenOnline === 'function' && typeof window.liveagent.showWhenOffline === 'function') {
        window._laq.push(() => {
          if (window && window.liveagent && typeof window.liveagent.showWhenOnline === 'function' && typeof window.liveagent.showWhenOffline === 'function') {
            window.liveagent.showWhenOnline(this.salesforceButtonId, document.getElementById(`liveagent_button_online_${this.salesforceButtonId}`));

            window.liveagent.showWhenOffline(this.salesforceButtonId, document.getElementById(`liveagent_button_offline_${this.salesforceButtonId}`));
          }
        });
      }

      if (userId && !this.isUserChat) {
        this.isUserChat = true;

        const {session} = this.props;
        const {user} = session;

        if (user) {
          const {name} = user;

          window.liveagent
            .setName(name);
        }


        // Set the UM_User_ID__c variable with the user id, in order to be sent to Salesforce chat
        // so that in CS they can know the details of the UM user in the chat
        window.liveagent
          .addCustomDetail('UM_User_ID__c', `user-${userId}`)
          .saveToTranscript('UM_User_ID__c');

        // Add UM_User_ID__c variable to the Contact info on the current chat session and then link
        // it to the Salesforce case
        window.liveagent
          .findOrCreate('Contact')
          .map('UM_User_ID__c', 'UM_User_ID__c', true, true, true);
        window.liveagent
          .findOrCreate('Contact')
          .saveToTranscript('ContactId')
          .showOnCreate()
          .linkToEntity('Case', 'ContactId');
      }

      if (!userId && this.isUserChat) {
        this.isUserChat = false;
      }

      // Set the Subject to the current chat session
      window.liveagent.addCustomDetail('Subject', 'Chat Session (WEB-B2C)');

      // Set the Case status of the current chat session
      window.liveagent.addCustomDetail('Case Status', 'New');

      // Link the Subject and Case Status to the current case
      window.liveagent
        .findOrCreate('Case')
        .map('Subject', 'Subject', true, false, true)
        .map('Status', 'Case Status', false, false, true)
        .saveToTranscript('CaseId')
        .showOnCreate();

      // Initialise the Salesforce liveagent
      window.liveagent.init(`https://d.${SALESFORCE_SCRIPT_URL}.salesforceliveagent.com/chat`, LIVEAGENT_INIT_ID_1, LIVEAGENT_INIT_ID_2);
    });
  }

  /*
   *  Open the chat window when the user clicks the chat button
   */
  @autobind
  @action('openChat - SalesforceChatButton')
  openChat(event) {
    event.preventDefault();
    const {session, tracker} = this.props;
    const {user} = session;

    tracker.track('Clicked to open chat - Online', {
      category: 'Chat',
      label: (user && user.id) || undefined,
    });

    this.isChatOpen = true;

    if (!this.hasStartedChat) {
      this.hasStartedChat = true;

      window.liveagent.startChatWithWindow(this.salesforceButtonId, 'chat-frame');
    }
  }

  /*
   *  Open the information popup if the user clicks the chat button and CS is offline
   */
  @autobind
  @action('openChatOffline - SalesforceChatButton')
  openChatOffline(event) {
    event.preventDefault();

    const {session, tracker} = this.props;
    const {user} = session;

    tracker.track('Clicked to open chat - Offline', {
      category: 'Chat',
      label: (user && user.id) || undefined,
    });

    this.showChatOfflinePopup = true;
  }

  @autobind
  @action('dismissPopup - SalesforceChatButton')
  dismissPopup(event) {
    event.preventDefault();

    this.showChatOfflinePopup = false;
  }

  @autobind
  @action('endChat - SalesforceChatButton')
  endChat(event) {
    event.preventDefault();

    this.isChatOpen = false;

    document.getElementById('chat-frame').src = '';
    this.hasStartedChat = false;
  }

  @autobind
  @action('minimiseChat - SalesforceChatButton')
  minimiseChat(event) {
    event.preventDefault();

    this.isChatOpen = false;
  }

  render() {
    const {
      csWorkingHours,
      isChatOpen,
      openChat,
      openChatOffline,
      props,
      showChatOfflinePopup,
      zendeskLocale,
    } = this;
    const {screen} = props;
    const {i18n} = this.context;

    return (
      <div>
        <a
          className="chat-button"
          id={`liveagent_button_online_${this.salesforceButtonId}`}
          href="#"
          onClick={openChat}
          style={{
            display: 'none',
          }}
        />
        <button
          className="chat-button"
          id={`liveagent_button_offline_${this.salesforceButtonId}`}
          style={{
            display: 'none',
          }}
          onClick={openChatOffline}
        />
        <div className={cn(
          'chat-button__iframe-container',
          isChatOpen && 'chat-button__iframe-container--active',
        )}>
          <div className="chat-button__popup-topbar">
            <button
              className="chat-button__minimise-button"
              onClick={this.minimiseChat}
            >
              <span className="chat-button__minimise-content" />
            </button>
            <button
              className="chat-button__end-chat-button"
              onClick={this.endChat}
            >
              {i18n._x('close chat', 'Salesforce chat')}
            </button>
          </div>

          <iframe className="chat-button__chat-container" title="chat-frame" name="chat-frame" id="chat-frame" />
        </div>
        {showChatOfflinePopup && !screen.isDesktop && (
          <Popup>
            <div className="salesforce__offline-popup">
              <img src={alertIcon} className="salesforce__offline-popup-icon" alt="" />
              <h1 className="salesforce__offline-popup-title">
                {i18n._x('Customer support is offline', 'Chat button')}
              </h1>
              <div className="salesforce__offline-popup-close-button" onClick={this.dismissPopup}>
                <img width="20" height="20" src={closeIcon} alt="" />
              </div>
              <div className="salesforce__offline-popup-body">
                <p className="salesforce__offline-popup-body-content">
                  {i18n._x(
                    'Our support team is online from %(workingHours)s.',
                    'Chat button',
                    {
                      workingHours: csWorkingHours,
                    },
                  )}
                </p>
                <p className="salesforce__offline-popup-body-content">
                  {i18n._x('Check our FAQs to answer questions or email us and we’ll get back to you as quickly as we can.', 'Chat button')}
                </p>
                <div className="salesforce__offline-popup-buttons-container row">
                  <div className="col-md-6 salesforce__offline-popup-buttons-container--left">
                    <a
                      className="
                        button
                        button--secondary
                        button--caps
                        button--normal
                        button--block
                        salesforce__offline-popup-button
                        salesforce__offline-popup-button-mail
                      "
                      onClick={this.onCloseToolTip}
                      href="mailto:info@urban.co"
                    >
                      <span>{i18n._x('Send Email', 'Chat button')}</span>
                    </a>
                  </div>
                  <div className="col-md-6 salesforce__offline-popup-buttons-container--right">
                    <a
                      className="
                        button
                        button--primary
                        button--caps
                        button--normal
                        button--block
                        salesforce__offline-popup-button
                      "
                      onClick={this.clickedUpgrade}
                      target="_blank" // eslint-disable-line react/jsx-no-target-blank
                      href={`${this.faqUrl}${zendeskLocale}`}
                    >
                      <span>{i18n._x('Check FAQ', 'Chat button')}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Popup>
        )}
        {showChatOfflinePopup && screen.isDesktop && (
          <div className="salesforce__offline-desktop-popup-container">
            <div className="salesforce__offline-popup">
              <img src={alertIcon} className="salesforce__offline-popup-icon" alt="" />
              <h1 className="salesforce__offline-popup-title">
                {i18n._x('Customer support is offline', 'Chat button')}
              </h1>
              <div className="salesforce__offline-popup-close-button" onClick={this.dismissPopup}>
                <img width="20" height="20" src={closeIcon} alt="" />
              </div>
              <div className="salesforce__offline-popup-body">
                <p className="salesforce__offline-popup-body-content">
                  {i18n._x(
                    'Our support team is online from %(workingHours)s.',
                    'Chat button',
                    {
                      workingHours: csWorkingHours,
                    },
                  )}
                </p>
                <p className="salesforce__offline-popup-body-content">
                  {i18n._x('Check our FAQs to answer questions or email us and we’ll get back to you as quickly as we can.', 'Chat button')}
                </p>
                <div className="salesforce__offline-popup-buttons-container row">
                  <div className="col-sm-6 salesforce__offline-popup-buttons-container--left">
                    <a
                      className="
                        button
                        button--secondary
                        button--caps
                        button--normal
                        button--block
                        salesforce__offline-popup-button
                        salesforce__offline-popup-button-mail
                      "
                      onClick={this.onCloseToolTip}
                      href="mailto:info@urban.co"
                    >
                      <span>{i18n._x('Send Email', 'Chat button')}</span>
                    </a>
                  </div>
                  <div className="col-sm-6 salesforce__offline-popup-buttons-container--right">
                    <a
                      className="
                        button
                        button--primary
                        button--caps
                        button--normal
                        button--block
                        salesforce__offline-popup-button
                      "
                      onClick={this.clickedUpgrade}
                      target="_blank" // eslint-disable-line react/jsx-no-target-blank
                      href={`${this.faqUrl}${zendeskLocale}`}
                    >
                      <span>{i18n._x('Check FAQ', 'Chat button')}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ChatButton;



// WEBPACK FOOTER //
// ./spa/Components/SalesforceChatButton.jsx